/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-28 16:03:18
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';

import ActionsGroupOnListing from 'src/components/views/synonym/actions-group-on-listing';
import ImportModal from './import';
import ExportModal from './export';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbeded from 'src/components/views/common/typeform-embedded';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import TitleHelp from 'src/components/views/common/title-help';
import DidYouKnow from 'src/components/views/synonym/did-you-know';
import history from 'src/routes/history';

import * as synonymActions from 'src/stores/actions/synonym-actions';
import ManualSynonymSetting from 'src/components/views/synonym/manual-synonym-setting';
import { NUMBER_SYNONYMS_PER_PAGE } from 'src/constants/synonym';
import { routes } from 'src/routes';

type Props = {
  getSynonyms: (q: string, skip: number, limit: number) => void,
  updateSynonyms: (synonyms: Array<Object>, actionName: string) => void,
  synonyms: Array<Object>,
  total: number,
  loading: boolean,
  success: boolean,
  updateSuccess: boolean,
  successFlag: number,
  saving: boolean,
  importing: boolean,
  scheduleSyncIn: number,
  waitSyncAt: number,
  selectedPlan: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeImportModal: boolean,
  activeExportModal: boolean,
  skip: number,
  limit: number,
  enabled: Object,
  fromSynonymPageState: number,
  scheduleSyncIn: number,
  youtubeModal: boolean
};

class ManualSynonym extends Component<ComposedProps, State> {
  timerTyping = undefined;

  state = {
    activeImportModal: false,
    activeExportModal: false,
    skip: 0,
    limit: NUMBER_SYNONYMS_PER_PAGE,
    enabled: {},
    fromSynonymPageState: 0,
    scheduleSyncIn: 0,
    youtubeModal: false,
    filter: {}
  };

  render() {
    const { i18n, synonyms, updateSuccess, selectedPlan } = this.props;
    const { skip, limit, fromSynonymPageState, youtubeModal, filter } = this.state;

    // Check having synonym
    const havingSynonyms = !_.isEmpty(synonyms);

    const docUrl = URL.Link_V1_Search_Synonym_ManualGuide;

    const isEmptyFilter = (filter.search || filter.status) && !havingSynonyms;
    return (
      <div className="BC-Page BC-Page__Synonym">
        <Page
          title={i18n.translate('AdminHTML.Synonym.Synonym.manualTab')}
          primaryAction={
            (havingSynonyms || isEmptyFilter) && (
              <div className="BC-Page BC-Page__Button">
                <ActionsGroupOnListing skip={skip} limit={limit} hideEdit={isEmptyFilter} />
              </div>
            )
          }>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={docUrl}
            onAnalytics={() => history.push(routes.analytics.overviewSearch())}
            onImport={this.handleChangeImportModal}
            onExport={this.handleChangeExportModal}
          />

          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Did you know */}
                <DidYouKnow />
                {/* Processing update banner */}
              </div>
            </Layout.Section>

            {/* List of synonym */}
            <Layout.Section>
              <ManualSynonymSetting setSynonymFilter={filter => this.setState({ filter })} />
            </Layout.Section>
          </Layout>
        </Page>

        <ImportModal
          active={this.state.activeImportModal}
          handleChange={this.handleChangeImportModal}
          getSynonymsFirstPage={this.getSynonymsFirstPage}
          history={history}
        />

        <ExportModal
          active={this.state.activeExportModal}
          handleChange={this.handleChangeExportModal}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbeded
            type="synonym"
            conditionShow={updateSuccess}
            conditionShowStateChange={fromSynonymPageState}
          />
        )}
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['SynonymsTut']}
          title={i18n.translate('AdminHTML.Synonym.Synonym.youtubeModal_Title')}
          onClose={this.handleCloseYoutubeModal}
        />
      </div>
    );
  }

  // Hide/show import modal
  handleChangeImportModal = () => {
    this.setState(({ activeImportModal }) => ({
      activeImportModal: !activeImportModal
    }));
  };

  // Hide/show export modal
  handleChangeExportModal = () => {
    this.setState(({ activeExportModal }) => ({
      activeExportModal: !activeExportModal
    }));
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSynonyms: (q, skip, limit) =>
    dispatch(synonymActions.getSynonyms({ q, skip, limit }, ownProps.i18n)),
  updateSynonyms: (synonyms, actionName) =>
    dispatch(synonymActions.updateSynonyms(synonyms, actionName, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    synonyms: _.get(state.synonym, 'synonyms', [{}, {}, {}]),
    total: _.get(state.synonym, 'total', 0),
    loading: _.get(state.synonym, 'loading', false),
    success: _.get(state.synonym, 'success', false),
    updateSuccess: _.get(state.synonym, 'updateSuccess', false),
    successFlag: _.get(state.synonym, 'successFlag', 0),
    saving: _.get(state.synonym, 'saving', false),
    importing: _.get(state.synonym, 'importing', false),
    scheduleSyncIn: _.get(state.synonym, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.synonym, 'waitSyncAt', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    )
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(ManualSynonym)),
  'SyncSynonym'
);
